import React from 'react';
import { Box, ClickAwayListener } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { TrackNowButton } from '../ui';
import CustomerContext from "../../context/customer/CustomerContext";
import AuthContext from "../../context/auth/AuthContext";
import usePermission from '../../usePermission';
import { useActiveBranding } from "../../theme/tokens";
import { useCommonDetailsContext } from "../../context/Common/common";

interface NavDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  activeSection: string;
  anchorElement: HTMLElement | null;
}

export const NavDropdown: React.FC<NavDropdownProps> = ({ isOpen, onClose, activeSection, anchorElement }) => {
  const navigate = useNavigate();
  const customerContext = React.useContext(CustomerContext);
  const { setCurrentCustomer } = customerContext;
  const authContext = React.useContext(AuthContext);
  const { authUser, roleType, roleDetails } = authContext;
  const { validateParentData } = useCommonDetailsContext();
  const activeBranding = useActiveBranding();
  const isDynamicBranding = !!activeBranding;

  const handleNavigation = (path: string) => {
    if (path === "/customer" || path === "/client-filing-tracker-list") {
      setCurrentCustomer && setCurrentCustomer(null);
    }
    if (path === "/client-filing-tracker-list") {
      localStorage.removeItem("selectedCustomer");
    }
    navigate(path);
    onClose();
  };

  if (!isOpen || !anchorElement) return null;

  const rect = anchorElement.getBoundingClientRect();
  const dropdownLeft = rect.left + (rect.width / 2);

  const showMenu = (menuName: any) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return usePermission(menuName, "showMenu");
  };

  const getMenuItems = () => {
    switch (activeSection) {
      case 'View Details':
        return [
          { label: 'Home', path: '/' },
          { label: 'TrackNow Master Filing List', path: '/filing-master-list', permissionName: 'MasterFilingList' },
          { label: 'TrackNow Deferred Filings', path: '/add-filings', permissionName: 'DefferedList' },
          { label: 'Post Award Master Filing List', path: '/postaward-filing-master-list', permissionName: 'PostAwardMasterList' },
          { label: 'Post Award Deferred Filings', path: '/add-filing-postaward', permissionName: 'PostAwardDefferedList' },
          { label: 'Client Tracker', path: '/client-filing-tracker-list', permissionName: 'ViewClientFilingTracker' },
          { label: 'Notification', path: '/notification' },
        ];
      case 'Workflows':
        return [
          { label: 'Add New Client', path: '/customer', permissionName: 'AddNewClient' },
          { label: 'Add New TrackNow Filing', path: '/filing-master', permissionName: 'AddFilingMaster' },
          { label: 'TrackNow Filing Master Approval', path: '/filing-master-approval-list', permissionName: 'FilingMasterApproval' },
          { label: 'TrackNow Client Filing Master Approval', path: '/client-filing-master-approval-list', permissionName: 'ClientFilingMasterApproval' },
          { label: 'Add New Post Award Master', path: '/postaward-master-form', permissionName: 'AddPostAwardMaster' },
          { label: 'Post Award Master Approval', path: '/postaward-approval-list', permissionName: 'PostAwardMasterApproval' },
          { label: 'Post Award Client Records Master Approval', path: '/PostAwardClientfilingMasterApprovalList', permissionName: 'PostAwardClientApproval' },
        ];
      case 'Configurations': {
        const isBrandingRoleIncludes = Array.isArray(roleType)
          ? roleType.some((role: string) =>
            [
              "Admin",
              "Admin-PostAward",
              "ClientAdmin",
              "ClientAdmin-PostAward",
              "HierarchyAdmin",
              "HierarchyAdmin-PostAward",
            ].includes(role)
          )
          : roleType
            ? [
              "Admin",
              "Admin-PostAward",
              "ClientAdmin",
              "ClientAdmin-PostAward",
              "HierarchyAdmin",
              "HierarchyAdmin-PostAward",
            ].includes(roleType)
            : false;

        const hasParentCustomerTypeForBrandAdmin = Array.isArray(roleDetails) && roleDetails.some(
          (item: any) =>
            [
              "ClientAdmin",
              "ClientAdmin-PostAward",
              "HierarchyAdmin",
              "HierarchyAdmin-PostAward",
            ].includes(item?.roleName) &&
            (item?.customerType?.toLowerCase() === "parent" || item?.clientType?.toLowerCase() === "parent")
        );

        const isSingleParent = Boolean(validateParentData?.isSingleParent ?? validateParentData?.ifSingleParent);
        const showBrandingPage = Boolean(
          (roleType && (roleType.includes("Admin") || roleType.includes("Admin-PostAward"))) ||
          (isBrandingRoleIncludes && hasParentCustomerTypeForBrandAdmin)
        );
        return [
          { label: 'TrackNow Business Category', path: '/business-category', permissionName: 'BusinessCategory' },
          { label: 'TrackNow Approval Configuration', path: '/ApprovalConfig', permissionName: 'ApprovalConfiguration' },
          { label: 'Post Award Program Configuration', path: '/ProgramNames', permissionName: "ProgramNames" },
          { label: 'Post Award Approval Configuration', path: '/PostAwardApproverConfiguration', permissionName: "PostAwardApprovalConfiguration" },
          { label: 'Notification Configuration', path: '/notificationConfiguration', permissionName: 'setting' },
          { label: 'Reminder Configuration', path: '/reminder', permissionName: 'setting' },
          ...(showBrandingPage
            ? [{ label: 'Branding Configuration', path: '/branding-configuration' }]
            : []),
          { label: 'Rollover Filings', path: '/rollOverFilings', permissionName: 'rollOver' },
          { label: 'User Role Setup', path: '/user-role-setup', permissionName: 'userRole' },
        ];
      }
      default:
        return [];
    }
  };

  const menuItems = getMenuItems();
  const visibleMenuItems = menuItems.filter((item: { permissionName?: string }) => !item.permissionName || showMenu(item.permissionName));

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Box
        sx={(theme) => ({
          position: 'fixed',
          top: '88px',
          left: `${dropdownLeft}px`,
          transform: `translateX(-50%) scaleY(${isOpen ? 1 : 0})`,
          transformOrigin: 'top center',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          width: 'fit-content',
          minWidth: '220px',
          padding: '4px',
          background: isDynamicBranding
            ? (activeBranding?.backgroundColor || theme.palette.background.default)
            : `linear-gradient(90deg, #1BA973 0%, #0897AB 25%, #5C50F3 75%, ${theme.palette.background.default} 100%)`,
          backgroundSize: isDynamicBranding ? 'auto' : '200% 100%',
          animation: (isOpen && !isDynamicBranding) ? 'gradientMove 3s ease infinite' : 'none',
          borderRadius: '0 0 10px 10px',
          zIndex: 1200,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          '@keyframes gradientMove': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        })}
      >
        {/* Extended Purple Bar for Active Section */}
        <Box
          sx={{
            backgroundColor: 'background.default',
            borderRadius: '0 0 8px 8px',
            padding: '20px 24px',
          }}
        >
          {/* Menu Items */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {visibleMenuItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  '& .MuiButton-root': {
                    color: 'white !important',
                    fontSize: '15px !important',
                    fontWeight: '400 !important',
                  },
                  '& svg': {
                    color: 'white !important',
                  }
                }}
              >
                <TrackNowButton
                  variant="compact"
                  onClick={() => handleNavigation(item.path)}
                >
                  {item.label}
                </TrackNowButton>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </ClickAwayListener>
  );
};
